import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { InstallHint } from '../components/InstallHint';
import { MarketCard } from '../components/MarketCard';
import { PastHangouts } from '../components/PastHangouts';
import { useCurrentHangout, useMyEntry } from '../hooks/useHangout';
import { useMarkets } from '../hooks/useMarkets';
import { getErrorMessage } from '../services/api-client';
import { useAuthStore } from '../stores/authStore';
import { colors } from '../themes/theme';

export function Board() {
    const session = useAuthStore((s) => s.session);
    const hangout = useCurrentHangout();
    const entry = useMyEntry(hangout.data?.id);
    const markets = useMarkets();

    if (hangout.isPending) return <Typography color="text.secondary">Loading…</Typography>;
    if (hangout.isError) return <Alert severity="error">{getErrorMessage(hangout.error)}</Alert>;

    if (!hangout.data) {
        return (
            <Stack spacing={2}>
                <InstallHint />
                <Typography variant="h2">Nothing running</Typography>
                <Typography color="text.secondary">
                    {session?.isAdmin
                        ? 'Open one and everybody is in automatically.'
                        : 'Nothing on at the moment.'}
                </Typography>
                {session?.isAdmin && (
                    <Button component={RouterLink} to="/admin" variant="contained" size="large">
                        Open one
                    </Button>
                )}
                <Typography variant="overline" sx={{ color: colors.muted, mt: 1 }}>
                    Previous nights
                </Typography>
                <PastHangouts />
            </Stack>
        );
    }

    const current = hangout.data;
    const live = (markets.data ?? []).filter((m) => m.status === 'open' || m.status === 'locked');
    const done = (markets.data ?? []).filter((m) => m.status === 'settled' || m.status === 'void');

    return (
        <Stack spacing={2}>
            <InstallHint />

            {/* The balance and the announcer live in the app's top bar. They
                shared this row until a long hangout name -- condensed,
                uppercase and unbreakable -- overflowed and covered the balance,
                which is the one number people keep checking. */}
            <Box>
                <Typography variant="overline" sx={{ color: colors.safety }}>
                    Tonight
                </Typography>
                <Typography variant="h2" sx={{ overflowWrap: 'anywhere' }}>
                    {current.name}
                </Typography>
                {current.venue && (
                    <Typography color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                        {current.venue}
                    </Typography>
                )}
            </Box>


            {entry.isSuccess && entry.data.eliminatedAt !== null && (
                <Alert severity="warning">
                    You&rsquo;re out for the night. Broke, not silent — you can still start
                    markets, settle them, and talk trash.
                </Alert>
            )}

            <Button component={RouterLink} to="/new" variant="contained" size="large">
                Start a bet
            </Button>

            {live.length === 0 && done.length === 0 && (
                <Typography color="text.secondary">
                    No bets yet. Somebody has to go first.
                </Typography>
            )}

            {live.map((market) => (
                <MarketCard key={market.id} market={market} />
            ))}

            {done.length > 0 && (
                <>
                    <Typography variant="overline" sx={{ color: colors.muted, mt: 1 }}>
                        Done
                    </Typography>
                    {done.map((market) => (
                        <MarketCard key={market.id} market={market} />
                    ))}
                </>
            )}
        </Stack>
    );
}
